import React, {useEffect, useState} from "react";
import {Link, LoaderFunctionArgs, useLoaderData, useNavigate} from "react-router-dom";
import axios from "axios";
import {useSession} from "./contexts/SessionContext";
import {DataGrid, GridActionsCellItem, GridColDef} from "@mui/x-data-grid";
import {AdminShift, LpChangeover, Meal, Plot, PlotEvent} from "../../server/Plot";
import {Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import {useTimezone} from "./contexts/TimezoneContext";
import {utcToLocalInput, localInputToUtc} from "./utils/timezone";
import {PlotPrint} from "./components/PlotPrint";

// ── Event Landing ────────────────────────────────────────────────────────────

export function EventLanding() {
    const tz = useTimezone()
    const [events, setEvents] = useState<PlotEvent[]>([])

    useEffect(() => {
        axios.get('/plot/events').then(({data}) => setEvents(Array.isArray(data) ? data : []))
    }, [])

    const eventListColumns: GridColDef[] = [
        {
            field: 'name', headerName: 'Event', flex: 2, renderCell: (params) => (
                <Link to={`./events/${params.row.id}`}>{params.value}</Link>
            )
        },
        {
            field: 'start', headerName: 'Start', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        {
            field: 'end', headerName: 'End', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
    ]

    return (
        <Paper elevation={2} sx={{p: 1}}>
            <Typography variant="h5" component="h5" align="center">Events</Typography>
            <DataGrid
                autoHeight
                rows={events}
                columns={eventListColumns}
                getRowId={(row) => row.id}
                density="compact"
                hideFooter={events.length <= 10}
                initialState={{pagination: {paginationModel: {pageSize: 10}}}}
            />
        </Paper>
    )
}

// ── Schedule Timeline ────────────────────────────────────────────────────────

const LEAD_PALETTE = [
    '#1565c0', '#2e7d32', '#6a1b9a', '#e65100',
    '#00838f', '#ad1457', '#4527a0', '#558b2f',
    '#f57f17', '#37474f',
]

const ADMIN_PALETTE = [
    '#7b1fa2', '#c62828', '#4e342e', '#1a237e',
    '#bf360c', '#006064', '#880e4f', '#33691e',
    '#e65100', '#263238',
]

const MEAL_BG = 'rgba(173, 216, 230, 0.45)'
const MEAL_BORDER = 'rgba(70, 160, 210, 0.8)'
const MEAL_TEXT = '#0d6fa8'

function peopleNeeded(plot: any): number {
    const hook = plot.hookId ? 1 : 0
    const npcs = plot.additional_characters?.additional_NPCS?.length ?? 0
    const monsters = (plot.additional_characters?.requested_monsters ?? [])
        .reduce((s: number, m: any) => s + (m.quantity ?? 0), 0)
    const dedicatedMarshals = (plot.additional_characters?.named_requested_monsters ?? [])
        .filter((m: any) => m.monster?.dedicated_marshal).length
    return hook + npcs + monsters + dedicatedMarshals
}

const ROW_H = 34
const AXIS_H = 24
const SECTION_LABEL_H = 28

function ScheduleTimeline({eventStart, eventEnd, plots, adminShifts, meals, lpChangeovers}: {
    eventStart: string, eventEnd: string, plots: Plot[], adminShifts: AdminShift[], meals: Meal[], lpChangeovers: LpChangeover[]
}) {
    const navigate = useNavigate()
    const tz = useTimezone()
    const startMs = new Date(eventStart).getTime()
    const endMs = new Date(eventEnd).getTime()
    const totalMs = endMs - startMs

    if (totalMs <= 0) return null

    const scheduled = plots.filter(p => p.start_time)

    // Build 3-hour tick marks aligned to 0/3/6/9/12/15/18
    const ticks: Date[] = []
    const cursor = new Date(startMs)
    cursor.setMinutes(0, 0, 0)
    cursor.setHours(Math.ceil(cursor.getHours() / 3) * 3)
    if (cursor.getTime() < startMs) cursor.setHours(cursor.getHours() + 3)
    while (cursor.getTime() <= endMs) {
        ticks.push(new Date(cursor))
        cursor.setHours(cursor.getHours() + 3)
    }

    // Assign a color per unique team lead
    const uniqueLeads = Array.from(new Set(scheduled.map(p => p.lead || '(unassigned)')))
    const leadColors: Record<string, string> = {}
    uniqueLeads.forEach((lead, i) => { leadColors[lead] = LEAD_PALETTE[i % LEAD_PALETTE.length] })

    // Assign a color per unique NPC admin
    const uniqueAdmins = Array.from(new Set(adminShifts.map(s => s.npcAdmin || '(unassigned)')))
    const adminColors: Record<string, string> = {}
    uniqueAdmins.forEach((admin, i) => { adminColors[admin] = ADMIN_PALETTE[i % ADMIN_PALETTE.length] })

    // Build 2am–8am off-hours bands for each day in the event window
    const offBands: { left: number, width: number }[] = []
    const dayStart = new Date(startMs)
    dayStart.setHours(0, 0, 0, 0)
    while (dayStart.getTime() < endMs) {
        const bandStart = new Date(dayStart); bandStart.setHours(2, 0, 0, 0)
        const bandEnd = new Date(dayStart); bandEnd.setHours(8, 0, 0, 0)
        const clampedStart = Math.max(bandStart.getTime(), startMs)
        const clampedEnd = Math.min(bandEnd.getTime(), endMs)
        if (clampedEnd > clampedStart) {
            offBands.push({
                left: (clampedStart - startMs) / totalMs * 100,
                width: (clampedEnd - clampedStart) / totalMs * 100,
            })
        }
        dayStart.setDate(dayStart.getDate() + 1)
    }

    const plotRows = Math.max(scheduled.length, 1)
    const adminSectionHeight = adminShifts.length > 0
        ? SECTION_LABEL_H + Math.max(uniqueAdmins.length, 1) * ROW_H + 8
        : 0
    const bodyHeight = plotRows * ROW_H + 8 + adminSectionHeight

    return (
        <Box sx={{overflowX: 'auto'}}>
            <Box sx={{position: 'relative', minWidth: 600, m: 3.5}}>
                {/* Axis */}
                <Box sx={{position: 'relative', height: AXIS_H, borderBottom: '1px solid', borderColor: 'divider'}}>
                    {offBands.map((band, i) => (
                        <Box key={`axis-off-${i}`} sx={{
                            position: 'absolute', left: `${band.left}%`, width: `${band.width}%`,
                            top: 0, bottom: 0, bgcolor: 'action.hover', pointerEvents: 'none'
                        }}/>
                    ))}
                    {ticks.map(t => {
                        const pct = (t.getTime() - startMs) / totalMs * 100
                        return (
                            <Typography key={t.getTime()} variant="caption" color="text.secondary"
                                        sx={{position: 'absolute', left: `${pct}%`, transform: 'translateX(-50%)', whiteSpace: 'nowrap'}}>
                                {t.toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})}
                            </Typography>
                        )
                    })}
                </Box>

                {/* Grid lines + rows */}
                <Box sx={{position: 'relative', height: bodyHeight}}>
                    {/* Off-hours bands */}
                    {offBands.map((band, i) => (
                        <Box key={`off-${i}`} sx={{
                            position: 'absolute', left: `${band.left}%`, width: `${band.width}%`,
                            top: 0, bottom: 0, bgcolor: 'action.hover', pointerEvents: 'none'
                        }}/>
                    ))}

                    {/* Meal blocks — rendered first so plots appear on top */}
                    {meals.map(meal => {
                        const mealStartMs = new Date(meal.startTime).getTime()
                        const mealEndMs = new Date(meal.endTime).getTime()
                        const leftPct = Math.max(0, (mealStartMs - startMs) / totalMs * 100)
                        const widthPct = Math.min((mealEndMs - mealStartMs) / totalMs * 100, 100 - leftPct)
                        const startLabel = new Date(meal.startTime).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})
                        const endLabel = new Date(meal.endTime).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})
                        return (
                            <Tooltip key={meal.id} title={
                                <span>{meal.name}<br/>{startLabel} – {endLabel}</span>
                            } arrow>
                                <Box sx={{
                                    position: 'absolute',
                                    left: `${leftPct}%`,
                                    width: `${Math.max(widthPct, 0.3)}%`,
                                    top: 0,
                                    bottom: 0,
                                    bgcolor: MEAL_BG,
                                    borderLeft: `2px solid ${MEAL_BORDER}`,
                                    borderRight: `2px solid ${MEAL_BORDER}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    pt: 0.5,
                                    overflow: 'hidden',
                                    pointerEvents: 'auto',
                                }}>
                                    <Typography variant="caption" sx={{
                                        color: MEAL_TEXT,
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        px: 0.25,
                                    }}>
                                        {meal.name}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )
                    })}

                    {/* LP Changeover lines — vertical red lines spanning full height, behind plot bars */}
                    {lpChangeovers.map(co => {
                        const coMs = new Date(co.time).getTime()
                        const leftPct = (coMs - startMs) / totalMs * 100
                        if (leftPct < 0 || leftPct > 100) return null
                        const timeLabel = new Date(co.time).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})
                        return (
                            <Tooltip key={co.id} title={<span>LP Changeover<br/>{timeLabel}</span>} arrow>
                                <Box sx={{
                                    position: 'absolute',
                                    left: `${leftPct}%`,
                                    top: 0,
                                    bottom: 0,
                                    width: '4px',
                                    transform: 'translateX(-1px)',
                                    bgcolor: '#d32f2f',
                                    pointerEvents: 'auto',
                                    cursor: 'default',
                                }}/>
                            </Tooltip>
                        )
                    })}

                    {/* Tick lines */}
                    {ticks.map(t => {
                        const pct = (t.getTime() - startMs) / totalMs * 100
                        return <Box key={t.getTime()} sx={{
                            position: 'absolute', left: `${pct}%`, top: 0, bottom: 0,
                            borderLeft: '1px dashed', borderColor: 'divider', pointerEvents: 'none'
                        }}/>
                    })}

                    {scheduled.length === 0 && adminShifts.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{pt: 1, pl: 1}}>
                            No plots have a start time assigned yet.
                        </Typography>
                    )}

                    {/* Plot bars */}
                    {scheduled.map((plot, i) => {
                        const plotStartMs = new Date(plot.start_time!).getTime()
                        const leftPct = Math.max(0, (plotStartMs - startMs) / totalMs * 100)
                        const durationMs = (plot.estimated_duration ?? 60) * 60 * 1000
                        const widthPct = Math.min(durationMs / totalMs * 100, 100 - leftPct)
                        const lead = plot.lead || '(unassigned)'
                        const color = leadColors[lead]
                        const startLabel = new Date(plot.start_time!).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})
                        const durationLabel = plot.estimated_duration ? `${Math.floor(plot.estimated_duration / 60)}h ${plot.estimated_duration % 60}m` : ''
                        const n = peopleNeeded(plot)

                        return (
                            <Tooltip key={plot.id} title={
                                <span>{plot.name}<br/>{startLabel}{durationLabel ? ` · ${durationLabel}` : ''}<br/>Lead: {lead}<br/>People needed: {n}</span>
                            } arrow>
                                <Box sx={{
                                    position: 'absolute',
                                    left: `${leftPct}%`,
                                    width: `${Math.max(widthPct, 0.5)}%`,
                                    top: i * ROW_H + 4,
                                    height: ROW_H - 8,
                                    bgcolor: color,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 0.75,
                                    overflow: 'hidden',
                                    opacity: 0.88,
                                    cursor: 'pointer',
                                    '&:hover': {opacity: 1}
                                }}
                                onClick={() => navigate(`/plot/${plot.id}`)}>
                                    <Typography variant="caption"
                                                sx={{color: '#fff', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1}}>
                                        {plot.name}
                                    </Typography>
                                    <Typography variant="caption"
                                                sx={{color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', ml: 0.5, opacity: 0.9}}>
                                        ×{n}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )
                    })}

                    {/* Admin Shifts section */}
                    {adminShifts.length > 0 && (
                        <>
                            {/* Section label band */}
                            <Box sx={{
                                position: 'absolute',
                                left: 0, right: 0,
                                top: plotRows * ROW_H + 8,
                                height: SECTION_LABEL_H,
                                bgcolor: 'action.selected',
                                display: 'flex', alignItems: 'center', pl: 1,
                                borderTop: '1px solid', borderColor: 'divider',
                            }}>
                                <Typography variant="caption" sx={{fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary'}}>
                                    Admin Shifts
                                </Typography>
                            </Box>

                            {/* Admin shift bars */}
                            {adminShifts.map((shift) => {
                                const shiftStartMs = new Date(shift.startTime).getTime()
                                const shiftEndMs = new Date(shift.endTime).getTime()
                                const leftPct = Math.max(0, (shiftStartMs - startMs) / totalMs * 100)
                                const widthPct = Math.min((shiftEndMs - shiftStartMs) / totalMs * 100, 100 - leftPct)
                                const admin = shift.npcAdmin || '(unassigned)'
                                const color = adminColors[admin]
                                const startLabel = new Date(shift.startTime).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})
                                const endLabel = new Date(shift.endTime).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit', timeZone: tz})
                                const adminRow = uniqueAdmins.indexOf(admin)
                                const topOffset = plotRows * ROW_H + 8 + SECTION_LABEL_H + adminRow * ROW_H + 4

                                return (
                                    <Tooltip key={shift.id} title={
                                        <span>NPC Admin: {admin}<br/>{startLabel} – {endLabel}</span>
                                    } arrow>
                                        <Box sx={{
                                            position: 'absolute',
                                            left: `${leftPct}%`,
                                            width: `${Math.max(widthPct, 0.5)}%`,
                                            top: topOffset,
                                            height: ROW_H - 8,
                                            bgcolor: color,
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 0.75,
                                            overflow: 'hidden',
                                            opacity: 0.88,
                                            '&:hover': {opacity: 1}
                                        }}>
                                            <Typography variant="caption"
                                                        sx={{color: '#fff', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                {admin}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )
                            })}
                        </>
                    )}
                </Box>

                {/* Legend */}
                <Box sx={{display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                    {Object.entries(leadColors).map(([lead, color]) => (
                        <Box key={lead} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: color}}/>
                            <Typography variant="caption" color="text.secondary">{lead}</Typography>
                        </Box>
                    ))}
                    {Object.keys(adminColors).length > 0 && Object.keys(leadColors).length > 0 && (
                        <Divider orientation="vertical" flexItem/>
                    )}
                    {Object.entries(adminColors).map(([admin, color]) => (
                        <Box key={`admin-${admin}`} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: color}}/>
                            <Typography variant="caption" color="text.secondary">{admin} (admin)</Typography>
                        </Box>
                    ))}
                    {meals.length > 0 && (Object.keys(leadColors).length > 0 || Object.keys(adminColors).length > 0) && (
                        <Divider orientation="vertical" flexItem/>
                    )}
                    {meals.length > 0 && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Box sx={{width: 12, height: 12, borderRadius: 0.5, bgcolor: MEAL_BORDER}}/>
                            <Typography variant="caption" color="text.secondary">Meal</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

// ── Event Viewer ─────────────────────────────────────────────────────────────

interface FaceCharEntry {
    id: number
    name: string
    plots: string[]
}

interface FaceCharGroup {
    player: string
    characters: FaceCharEntry[]
}

function buildFaceCharGroups(plots: Plot[]): FaceCharGroup[] {
    const map = new Map<number, {id: number, name: string, playedBy: string, plots: string[]}>()

    for (const plot of plots) {
        const raw = plot as any
        const candidates: any[] = []
        if (raw.hook?.type === 'FACEROLE') candidates.push(raw.hook)
        for (const npc of (raw.additional_characters?.additional_NPCS ?? [])) {
            if (npc.type === 'FACEROLE') candidates.push(npc)
        }
        for (const npc of candidates) {
            if (!map.has(npc.id)) {
                map.set(npc.id, {id: npc.id, name: npc.name, playedBy: npc.extended?.played_by ?? '', plots: []})
            }
            const entry = map.get(npc.id)!
            if (!entry.plots.includes(plot.name)) entry.plots.push(plot.name)
        }
    }

    const grouped = new Map<string, FaceCharEntry[]>()
    for (const {id, name, playedBy, plots} of map.values()) {
        const player = playedBy || '(Unassigned)'
        if (!grouped.has(player)) grouped.set(player, [])
        grouped.get(player)!.push({id, name, plots})
    }

    return Array.from(grouped.entries())
        .sort(([a], [b]) => a === '(Unassigned)' ? 1 : b === '(Unassigned)' ? -1 : a.localeCompare(b))
        .map(([player, characters]) => ({player, characters: characters.sort((a, b) => a.name.localeCompare(b.name))}))
}

function plotMatchesUser(plot: Plot, displayName: string): boolean {
    if (!displayName) return false
    if (plot.lead === displayName) return true
    const raw = plot as any
    if (raw.hook?.extended?.played_by === displayName) return true
    return (raw.additional_characters?.additional_NPCS ?? []).some(
        (npc: any) => npc.extended?.played_by === displayName
    )
}

export const eventLoader = async ({params}: LoaderFunctionArgs): Promise<any> => {
    if (typeof params.id !== 'undefined') {
        return axios.get(`/plot/events/${params.id}`).then(({data}) => data)
    }
    return null
}

interface ShiftForm {
    npcAdmin: string
    startTime: string
    endTime: string
}

interface MealForm {
    name: string
    startTime: string
    endTime: string
}

export function EventViewer() {
    const loaded = useLoaderData() as (PlotEvent & { plots: Plot[], adminShifts: AdminShift[], meals: Meal[], lpChangeovers: LpChangeover[] })
    const tz = useTimezone()
    const [plots, setPlots] = useState<Plot[]>(loaded?.plots ?? [])
    const [editingPlot, setEditingPlot] = useState<Plot | null>(null)
    const [editStartTime, setEditStartTime] = useState('')

    const [adminShifts, setAdminShifts] = useState<AdminShift[]>(loaded?.adminShifts ?? [])
    const {roles, user: sessionUser} = useSession()
    const canManage = roles.includes('GM') || roles.includes('Owner')
    const [mySchedule, setMySchedule] = useState(false)
    const currentDisplayName = sessionUser?.display_name ?? ''
    const [shiftDialogOpen, setShiftDialogOpen] = useState(false)
    const [editingShift, setEditingShift] = useState<AdminShift | null>(null)
    const [shiftForm, setShiftForm] = useState<ShiftForm>({npcAdmin: '', startTime: '', endTime: ''})

    const [meals, setMeals] = useState<Meal[]>(loaded?.meals ?? [])
    const [mealDialogOpen, setMealDialogOpen] = useState(false)
    const [editingMeal, setEditingMeal] = useState<Meal | null>(null)
    const [mealForm, setMealForm] = useState<MealForm>({name: '', startTime: '', endTime: ''})

    const [lpChangeovers, setLpChangeovers] = useState<LpChangeover[]>(loaded?.lpChangeovers ?? [])
    const [changoverDialogOpen, setChangeoverDialogOpen] = useState(false)
    const [editingChangeover, setEditingChangeover] = useState<LpChangeover | null>(null)
    const [changeoverTime, setChangeoverTime] = useState('')
    const [printingAllPlots, setPrintingAllPlots] = useState(false)

    if (!loaded) return null

    async function printAllPlots() {
        setPrintingAllPlots(true)
        try {
            const fullPlots = await Promise.all(
                plots.map(p => axios.get(`/plot/plots/${p.id}`).then(({data}) => data))
            )
            const win = window.open('', '_blank', 'width=900,height=1100')
            if (!win) return
            const printedAt = new Date().toLocaleString()
            const html = document.createElement('html')
            const head = document.createElement('head')
            head.innerHTML = `<style>
                @page {
                    size: 8.5in 11in;
                    margin: 0.5in;
                    @bottom-left {
                        content: "Printed: ${printedAt}";
                        font-family: Arial, sans-serif;
                        font-size: 9pt;
                        color: #666;
                    }
                }
                body { margin: 0; padding: 0; background: white; }
                * { box-sizing: border-box; }
            </style>`
            const body = document.createElement('body')
            const root = document.createElement('div')
            body.appendChild(root)
            html.appendChild(head)
            html.appendChild(body)
            win.document.replaceChild(html, win.document.documentElement)
            import('react-dom/client').then(({createRoot}) => {
                const reactRoot = createRoot(root)
                reactRoot.render(
                    <>
                        {fullPlots.map((plot: Plot) => (
                            <div key={plot.id} style={{breakAfter: 'page', pageBreakAfter: 'always'}}>
                                <PlotPrint
                                    plot={plot}
                                    plotUrl={`${window.location.origin}/plot/${plot.id}`}
                                />
                            </div>
                        ))}
                    </>
                )
                setTimeout(() => {
                    win.focus()
                    win.print()
                }, 800 + fullPlots.length * 100)
            })
        } finally {
            setPrintingAllPlots(false)
        }
    }

    const visiblePlots = mySchedule
        ? plots.filter(p => plotMatchesUser(p, currentDisplayName))
        : plots
    const visibleShifts = mySchedule
        ? adminShifts.filter(s => s.npcAdmin === currentDisplayName)
        : adminShifts
    const faceCharGroups = buildFaceCharGroups(plots)

    function openEdit(plot: Plot) {
        setEditingPlot(plot)
        setEditStartTime(utcToLocalInput(plot.start_time, tz))
    }

    async function saveStartTime() {
        if (!editingPlot) return
        const utcValue = editStartTime ? localInputToUtc(editStartTime, tz) : null
        await axios.put(`/plot/plots/${editingPlot.id}/start-time`, {
            start_time: utcValue
        }).then(({data}) => {
            setPlots(prev => prev.map(p => p.id === data.id ? {...p, start_time: data.start_time} : p))
            setEditingPlot(null)
        })
    }

    function openCreateShift() {
        setEditingShift(null)
        setShiftForm({
            npcAdmin: '',
            startTime: loaded.start ? utcToLocalInput(loaded.start, tz) : '',
            endTime: loaded.end ? utcToLocalInput(loaded.end, tz) : '',
        })
        setShiftDialogOpen(true)
    }

    function openEditShift(shift: AdminShift) {
        setEditingShift(shift)
        setShiftForm({
            npcAdmin: shift.npcAdmin,
            startTime: utcToLocalInput(shift.startTime, tz),
            endTime: utcToLocalInput(shift.endTime, tz),
        })
        setShiftDialogOpen(true)
    }

    async function saveShift() {
        const utcStart = localInputToUtc(shiftForm.startTime, tz)
        const utcEnd = localInputToUtc(shiftForm.endTime, tz)
        if (editingShift) {
            await axios.put(`/plot/admin-shifts/${editingShift.id}`, {
                npcAdmin: shiftForm.npcAdmin,
                startTime: utcStart,
                endTime: utcEnd,
            }).then(({data}) => {
                setAdminShifts(prev => prev.map(s => s.id === data.id ? data : s))
                setShiftDialogOpen(false)
            })
        } else {
            await axios.post(`/plot/events/${loaded.id}/admin-shifts`, {
                npcAdmin: shiftForm.npcAdmin,
                startTime: utcStart,
                endTime: utcEnd,
            }).then(({data}) => {
                setAdminShifts(prev => [...prev, data])
                setShiftDialogOpen(false)
            })
        }
    }

    async function removeShift(id: number) {
        await axios.delete(`/plot/admin-shifts/${id}`).then(() => {
            setAdminShifts(prev => prev.filter(s => s.id !== id))
        })
    }

    function openCreateMeal() {
        setEditingMeal(null)
        setMealForm({
            name: '',
            startTime: loaded.start ? utcToLocalInput(loaded.start, tz) : '',
            endTime: loaded.end ? utcToLocalInput(loaded.end, tz) : '',
        })
        setMealDialogOpen(true)
    }

    function openEditMeal(meal: Meal) {
        setEditingMeal(meal)
        setMealForm({
            name: meal.name,
            startTime: utcToLocalInput(meal.startTime, tz),
            endTime: utcToLocalInput(meal.endTime, tz),
        })
        setMealDialogOpen(true)
    }

    async function saveMeal() {
        const utcStart = localInputToUtc(mealForm.startTime, tz)
        const utcEnd = localInputToUtc(mealForm.endTime, tz)
        if (editingMeal) {
            await axios.put(`/plot/meals/${editingMeal.id}`, {
                name: mealForm.name,
                startTime: utcStart,
                endTime: utcEnd,
            }).then(({data}) => {
                setMeals(prev => prev.map(m => m.id === data.id ? data : m))
                setMealDialogOpen(false)
            })
        } else {
            await axios.post(`/plot/events/${loaded.id}/meals`, {
                name: mealForm.name,
                startTime: utcStart,
                endTime: utcEnd,
            }).then(({data}) => {
                setMeals(prev => [...prev, data])
                setMealDialogOpen(false)
            })
        }
    }

    async function removeMeal(id: number) {
        await axios.delete(`/plot/meals/${id}`).then(() => {
            setMeals(prev => prev.filter(m => m.id !== id))
        })
    }

    function openCreateChangeover() {
        setEditingChangeover(null)
        setChangeoverTime(loaded.start ? utcToLocalInput(loaded.start, tz) : '')
        setChangeoverDialogOpen(true)
    }

    function openEditChangeover(co: LpChangeover) {
        setEditingChangeover(co)
        setChangeoverTime(utcToLocalInput(co.time, tz))
        setChangeoverDialogOpen(true)
    }

    async function saveChangeover() {
        const utcTime = localInputToUtc(changeoverTime, tz)
        if (editingChangeover) {
            await axios.put(`/plot/lp-changeovers/${editingChangeover.id}`, {time: utcTime}).then(({data}) => {
                setLpChangeovers(prev => prev.map(c => c.id === data.id ? data : c))
                setChangeoverDialogOpen(false)
            })
        } else {
            await axios.post(`/plot/events/${loaded.id}/lp-changeovers`, {time: utcTime}).then(({data}) => {
                setLpChangeovers(prev => [...prev, data].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()))
                setChangeoverDialogOpen(false)
            })
        }
    }

    async function removeChangeover(id: number) {
        await axios.delete(`/plot/lp-changeovers/${id}`).then(() => {
            setLpChangeovers(prev => prev.filter(c => c.id !== id))
        })
    }

    const columns: GridColDef[] = [
        {
            field: 'name', headerName: 'Plot', flex: 2, renderCell: (params) => (
                <Link to={`/plot/${params.row.id}`}>{params.value}</Link>
            )
        },
        {field: 'writer', headerName: 'Writer', flex: 1},
        {field: 'lead', headerName: 'Team Lead', flex: 1},
        {
            field: 'start_time', headerName: 'Start Time', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        {
            field: 'people_needed', headerName: 'People Needed', width: 130,
            headerAlign: 'center', align: 'center',
            valueGetter: (params: any) => peopleNeeded(params.row)
        },
        {
            field: 'actions', type: 'actions', width: 50,
            getActions: (params) => [
                <GridActionsCellItem icon={<EditIcon/>} label="Edit Start Time" onClick={() => openEdit(params.row as Plot)}/>
            ]
        }
    ]

    const shiftColumns: GridColDef[] = [
        {field: 'npcAdmin', headerName: 'NPC Administrator', flex: 2},
        {
            field: 'startTime', headerName: 'Start Time', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        {
            field: 'endTime', headerName: 'End Time', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        ...(canManage ? [{
            field: 'actions', type: 'actions' as const, width: 80,
            getActions: (params: any) => [
                <GridActionsCellItem icon={<EditIcon/>} label="Edit" onClick={() => openEditShift(params.row as AdminShift)}/>,
                <GridActionsCellItem icon={<DeleteIcon/>} label="Delete" onClick={() => removeShift(params.row.id)}/>,
            ]
        }] : [])
    ]

    const mealColumns: GridColDef[] = [
        {field: 'name', headerName: 'Meal', flex: 2},
        {
            field: 'startTime', headerName: 'Start Time', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        {
            field: 'endTime', headerName: 'End Time', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        ...(canManage ? [{
            field: 'actions', type: 'actions' as const, width: 80,
            getActions: (params: any) => [
                <GridActionsCellItem icon={<EditIcon/>} label="Edit" onClick={() => openEditMeal(params.row as Meal)}/>,
                <GridActionsCellItem icon={<DeleteIcon/>} label="Delete" onClick={() => removeMeal(params.row.id)}/>,
            ]
        }] : [])
    ]

    return <React.Fragment>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">{loaded.name}</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="subtitle1" color="text.secondary">Start</Typography>
                    <Typography>{loaded.start ? new Date(loaded.start).toLocaleString(undefined, {timeZone: tz}) : '—'}</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="subtitle1" color="text.secondary">End</Typography>
                    <Typography>{loaded.end ? new Date(loaded.end).toLocaleString(undefined, {timeZone: tz}) : '—'}</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                <ToggleButtonGroup
                    value={mySchedule ? 'my' : 'full'}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val !== null) setMySchedule(val === 'my') }}
                >
                    <ToggleButton value="full">Full Schedule</ToggleButton>
                    <ToggleButton value="my">My Schedule</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            {loaded.start && loaded.end && (
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h5" align="center" gutterBottom>Schedule</Typography>
                        <ScheduleTimeline
                            eventStart={loaded.start} eventEnd={loaded.end}
                            plots={visiblePlots} adminShifts={visibleShifts} meals={meals} lpChangeovers={lpChangeovers}
                        />
                    </Paper>
                </Grid>
            )}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Typography variant="h5" sx={{flex: 1, textAlign: 'center'}}>Plots</Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={printingAllPlots ? <CircularProgress size={16}/> : <PrintIcon/>}
                            onClick={printAllPlots}
                            disabled={plots.length === 0 || printingAllPlots}
                        >
                            {printingAllPlots ? 'Fetching…' : 'Print All Plot Documents'}
                        </Button>
                    </Box>
                    <DataGrid
                        autoHeight
                        rows={visiblePlots}
                        columns={columns}
                        getRowId={(row) => row.id}
                        density="compact"
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h5" align="center" gutterBottom>Face Characters</Typography>
                    {faceCharGroups.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
                            No face characters assigned to this event.
                        </Typography>
                    ) : faceCharGroups.map(({player, characters}) => (
                        <Box key={player} sx={{mb: 1.5}}>
                            <Typography variant="subtitle2" sx={{
                                fontWeight: 700, px: 1, py: 0.5,
                                bgcolor: 'action.hover', borderRadius: 1, mb: 0.5
                            }}>
                                {player}
                            </Typography>
                            {characters.map(char => (
                                <Box key={char.id} sx={{display: 'flex', gap: 2, px: 2, py: 0.25, alignItems: 'baseline'}}>
                                    <Link to={`/npc/${char.id}`} style={{minWidth: 160, fontWeight: 500}}>
                                        {char.name}
                                    </Link>
                                    <Typography variant="caption" color="text.secondary">
                                        {char.plots.join(' · ')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Typography variant="h5" align="center" sx={{flex: 1}}>Admin Shifts</Typography>
                        {canManage && (
                            <Button variant="contained" size="small" onClick={openCreateShift}>
                                Add Admin Shift
                            </Button>
                        )}
                    </Box>
                    <DataGrid
                        autoHeight
                        rows={visibleShifts}
                        columns={shiftColumns}
                        getRowId={(row) => row.id}
                        density="compact"
                        hideFooter={visibleShifts.length <= 10}
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Typography variant="h5" align="center" sx={{flex: 1}}>Meals</Typography>
                        {canManage && (
                            <Button variant="contained" size="small" onClick={openCreateMeal}>
                                Add Meal
                            </Button>
                        )}
                    </Box>
                    <DataGrid
                        autoHeight
                        rows={meals}
                        columns={mealColumns}
                        getRowId={(row) => row.id}
                        density="compact"
                        hideFooter={meals.length <= 10}
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Typography variant="h5" align="center" sx={{flex: 1}}>LP Changeovers</Typography>
                        {canManage && (
                            <Button variant="contained" size="small" onClick={openCreateChangeover}>
                                Add LP Changeover
                            </Button>
                        )}
                    </Box>
                    <DataGrid
                        autoHeight
                        rows={lpChangeovers}
                        columns={[
                            {
                                field: 'time', headerName: 'Time', flex: 2,
                                valueFormatter: (params: any) => params.value
                                    ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
                            },
                            ...(canManage ? [{
                                field: 'actions', type: 'actions' as const, width: 80,
                                getActions: (params: any) => [
                                    <GridActionsCellItem icon={<EditIcon/>} label="Edit" onClick={() => openEditChangeover(params.row as LpChangeover)}/>,
                                    <GridActionsCellItem icon={<DeleteIcon/>} label="Delete" onClick={() => removeChangeover(params.row.id)}/>,
                                ]
                            }] : [])
                        ]}
                        getRowId={(row) => row.id}
                        density="compact"
                        hideFooter={lpChangeovers.length <= 10}
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h5" align="center" gutterBottom>Monsters Needed</Typography>
                    {(() => {
                        type MonsterRow = {id: number, monsterId: number, name: string, ace: string, unique: boolean, dedicated_marshal: boolean}
                        const rows: MonsterRow[] = Array.from(new Map<number, MonsterRow>(
                            (plots as any[]).flatMap(p =>
                                (p.additional_characters?.named_requested_monsters ?? []).map((m: any) => [
                                    m.monster.id as number,
                                    {id: m.monster.id, monsterId: m.monster.id, name: m.monster.name, ace: m.monster.attributes?.ace ?? '', unique: m.monster.unique, dedicated_marshal: m.monster.dedicated_marshal ?? false}
                                ])
                            )
                        ).values())
                        if (rows.length === 0) return (
                            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
                                No monsters assigned to plots for this event.
                            </Typography>
                        )
                        const monsterCols: GridColDef[] = [
                            {
                                field: 'name', headerName: 'Monster', flex: 2,
                                renderCell: (params) => roles.includes('MonsterManager')
                                    ? <Link to={`/monsters/${params.row.monsterId}`}>{params.value}</Link>
                                    : params.value
                            },
                            {field: 'ace', headerName: 'ACE', flex: 1},
                            {
                                field: 'unique', headerName: 'Unique', width: 90,
                                headerAlign: 'center', align: 'center',
                                renderCell: (params) => params.value ? '✓' : ''
                            },
                            {
                                field: 'dedicated_marshal', headerName: 'Ded. Marshal', width: 130,
                                headerAlign: 'center', align: 'center',
                                renderCell: (params) => params.value ? <Chip label="Ded. Marshal" color="secondary" size="small"/> : ''
                            },
                        ]
                        return (
                            <DataGrid
                                autoHeight
                                rows={rows}
                                columns={monsterCols}
                                getRowId={(row) => row.id}
                                density="compact"
                                hideFooter={rows.length <= 25}
                                initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                            />
                        )
                    })()}
                </Paper>
            </Grid>
        </Grid>


        {/* Edit plot start time dialog */}
        <Dialog open={!!editingPlot} onClose={() => setEditingPlot(null)}>
            <DialogTitle>Edit Start Time — {editingPlot?.name}</DialogTitle>
            <DialogContent sx={{pt: 2}}>
                <TextField
                    fullWidth
                    label={`Plot Start Time (${tz})`}
                    type="datetime-local"
                    value={editStartTime}
                    InputLabelProps={{shrink: true}}
                    inputProps={{
                        min: loaded.start ? utcToLocalInput(loaded.start, tz) : undefined,
                        max: loaded.end ? utcToLocalInput(loaded.end, tz) : undefined,
                    }}
                    onChange={ev => setEditStartTime(ev.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEditingPlot(null)}>Cancel</Button>
                <Button variant="contained" onClick={saveStartTime}>Save</Button>
            </DialogActions>
        </Dialog>

        {/* Create/edit admin shift dialog */}
        <Dialog open={shiftDialogOpen} onClose={() => setShiftDialogOpen(false)}>
            <DialogTitle>{editingShift ? 'Edit Admin Shift' : 'Add Admin Shift'}</DialogTitle>
            <DialogContent sx={{pt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                <TextField
                    fullWidth
                    label="NPC Administrator"
                    value={shiftForm.npcAdmin}
                    onChange={ev => setShiftForm(f => ({...f, npcAdmin: ev.target.value}))}
                />
                <TextField
                    fullWidth
                    label={`Start Time (${tz})`}
                    type="datetime-local"
                    value={shiftForm.startTime}
                    InputLabelProps={{shrink: true}}
                    inputProps={{
                        min: loaded.start ? utcToLocalInput(loaded.start, tz) : undefined,
                        max: loaded.end ? utcToLocalInput(loaded.end, tz) : undefined,
                    }}
                    onChange={ev => setShiftForm(f => ({...f, startTime: ev.target.value}))}
                />
                <TextField
                    fullWidth
                    label={`End Time (${tz})`}
                    type="datetime-local"
                    value={shiftForm.endTime}
                    InputLabelProps={{shrink: true}}
                    inputProps={{
                        min: loaded.start ? utcToLocalInput(loaded.start, tz) : undefined,
                        max: loaded.end ? utcToLocalInput(loaded.end, tz) : undefined,
                    }}
                    onChange={ev => setShiftForm(f => ({...f, endTime: ev.target.value}))}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShiftDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={saveShift} disabled={!shiftForm.npcAdmin.trim() || !shiftForm.startTime || !shiftForm.endTime}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>

        {/* Create/edit meal dialog */}
        <Dialog open={mealDialogOpen} onClose={() => setMealDialogOpen(false)}>
            <DialogTitle>{editingMeal ? 'Edit Meal' : 'Add Meal'}</DialogTitle>
            <DialogContent sx={{pt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                <TextField
                    fullWidth
                    label="Meal Name"
                    value={mealForm.name}
                    onChange={ev => setMealForm(f => ({...f, name: ev.target.value}))}
                />
                <TextField
                    fullWidth
                    label={`Start Time (${tz})`}
                    type="datetime-local"
                    value={mealForm.startTime}
                    InputLabelProps={{shrink: true}}
                    inputProps={{
                        min: loaded.start ? utcToLocalInput(loaded.start, tz) : undefined,
                        max: loaded.end ? utcToLocalInput(loaded.end, tz) : undefined,
                    }}
                    onChange={ev => setMealForm(f => ({...f, startTime: ev.target.value}))}
                />
                <TextField
                    fullWidth
                    label={`End Time (${tz})`}
                    type="datetime-local"
                    value={mealForm.endTime}
                    InputLabelProps={{shrink: true}}
                    inputProps={{
                        min: loaded.start ? utcToLocalInput(loaded.start, tz) : undefined,
                        max: loaded.end ? utcToLocalInput(loaded.end, tz) : undefined,
                    }}
                    onChange={ev => setMealForm(f => ({...f, endTime: ev.target.value}))}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setMealDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={saveMeal} disabled={!mealForm.name.trim() || !mealForm.startTime || !mealForm.endTime}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
        {/* Create/edit LP changeover dialog */}
        <Dialog open={changoverDialogOpen} onClose={() => setChangeoverDialogOpen(false)}>
            <DialogTitle>{editingChangeover ? 'Edit LP Changeover' : 'Add LP Changeover'}</DialogTitle>
            <DialogContent sx={{pt: 2}}>
                <TextField
                    fullWidth
                    label={`Time (${tz})`}
                    type="datetime-local"
                    value={changeoverTime}
                    InputLabelProps={{shrink: true}}
                    inputProps={{
                        min: loaded.start ? utcToLocalInput(loaded.start, tz) : undefined,
                        max: loaded.end ? utcToLocalInput(loaded.end, tz) : undefined,
                    }}
                    onChange={ev => setChangeoverTime(ev.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setChangeoverDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={saveChangeover} disabled={!changeoverTime}>Save</Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}
